import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowRight, ChevronLeft, Users, Heart, CreditCard, Check, Home, Coins } from 'lucide-react';
import './DonatePage.css';

const NEDARIM_CONFIG = {
    Mosad: '7001292',
    ApiValid: 'LgbGNqwumA',
    Currency: '1',
};

const VAT_RATE = 0.18;

const MATANOT_PRESETS = [
    { value: 36, label: '' },
    { value: 50, label: 'ארוחה ל-2' },
    { value: 100, label: '' },
    { value: 180, label: '' },
    { value: 260, label: '' },
    { value: 360, label: '' },
];

const TOTAL_STEPS = 5;

const DonatePage = ({ silverPrice, onBack, initialType, defaultGrams = 9 }) => {
    // Wizard state
    const [step, setStep] = useState(1);
    const [donationType, setDonationType] = useState(initialType || null); // 'machatzit' | 'matanot'

    // Machatzit state
    const [nefashot, setNefashot] = useState(1);
    const [includeVat, setIncludeVat] = useState(true);
    const [gramsOption, setGramsOption] = useState(defaultGrams); // 9 or 9.6

    // Matanot state
    const [matamotAmount, setMatamotAmount] = useState(50);
    const [customAmount, setCustomAmount] = useState('');

    // Personal info
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [idNumber, setIdNumber] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [dedication, setDedication] = useState('');

    // Validation
    const [errors, setErrors] = useState({});

    // Payment
    const [showIframe, setShowIframe] = useState(false);
    const [iframeLoaded, setIframeLoaded] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [paymentError, setPaymentError] = useState(null);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const iframeRef = useRef(null);
    const timeoutRef = useRef(null);

    // If initialType is set (cross-sell), auto-advance
    useEffect(() => {
        if (initialType) {
            setDonationType(initialType);
            setStep(2);
        }
    }, [initialType]);

    // Dynamic SEO title for donate page
    useEffect(() => {
        const typeLabel = donationType === 'machatzit' ? 'זכר למחצית השקל' : donationType === 'matanot' ? 'מתנות לאביונים' : 'תרומה';
        document.title = `תרומה — ${typeLabel} | כולל שערי ניסים — פורים תשפ״ו`;
        return () => {
            document.title = 'זכר למחצית השקל תשפ״ו — שווי מחצית השקל בזמן אמת | מחיר כסף עדכני 2026';
        };
    }, [donationType]);

    // Calculate amounts
    const pricePerGram = silverPrice || 0;
    const baseAmount = pricePerGram * gramsOption * nefashot;
    const machatzitAmount = includeVat ? baseAmount * (1 + VAT_RATE) : baseAmount;
    const finalAmount = donationType === 'machatzit'
        ? Math.round(machatzitAmount * 100) / 100
        : matamotAmount;

    const formatCurrency = (amount) => {
        if (!amount || isNaN(amount)) return '---';
        return new Intl.NumberFormat('he-IL', {
            style: 'currency',
            currency: 'ILS',
            minimumFractionDigits: 2,
        }).format(amount);
    };

    // Validation
    const validateStep3 = () => {
        const newErrors = {};
        if (!firstName.trim()) newErrors.firstName = 'שדה חובה';
        if (!lastName.trim()) newErrors.lastName = 'שדה חובה';
        if (!phone.trim()) newErrors.phone = 'שדה חובה';
        else if (!/^0[0-9]{8,9}$/.test(phone)) newErrors.phone = 'מספר טלפון לא תקין';
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = 'כתובת מייל לא תקינה';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Navigation
    const goNext = () => {
        if (step === 1 && !donationType) return;
        if (step === 3 && !validateStep3()) return;
        if (step === 3) {
            // Moving to payment step
            setStep(4);
            setPaymentError(null);
            setIsProcessing(false);
            setShowIframe(false);
            // Load iframe after a short delay
            setTimeout(() => {
                setShowIframe(true);
                setTimeout(() => setIframeLoaded(true), 1200);
            }, 300);
            return;
        }
        setStep((s) => Math.min(s + 1, TOTAL_STEPS));
    };

    const goBack = () => {
        if (step === 1) { onBack(); return; }
        if (step === 4) {
            setShowIframe(false);
            setIframeLoaded(false);
        }
        setStep((s) => s - 1);
    };

    // Payment logic
    const handlePostMessage = useCallback((event) => {
        if (event.origin !== 'https://www.matara.pro') return;
        try {
            const data = event.data;
            if (data.Name === 'TransactionResponse') {
                if (timeoutRef.current) clearTimeout(timeoutRef.current);
                const response = data.Value;
                setIsProcessing(false);
                if (response && (response.Status === 'True' || response.Status === 'OK')) {
                    setPaymentSuccess(true);
                    setStep(5);
                } else {
                    setPaymentError('התשלום נכשל: ' + (response?.Message || response?.ErrorMessage || 'אירעה שגיאה'));
                }
            }
            if (data.Name === 'Error') {
                if (timeoutRef.current) clearTimeout(timeoutRef.current);
                setIsProcessing(false);
                setPaymentError('שגיאה: ' + (data.Value || 'אירעה שגיאה'));
            }
            if (data.Name === 'RedirectTo') {
                const url = data.Value;
                if (url) window.open(url, '_blank');
            }
        } catch (err) {
            console.error('Error processing iframe message:', err);
        }
    }, []);

    useEffect(() => {
        window.addEventListener('message', handlePostMessage);
        return () => {
            window.removeEventListener('message', handlePostMessage);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [handlePostMessage]);

    const executePayment = () => {
        if (!iframeRef.current) return;
        setPaymentError(null);
        setIsProcessing(true);

        timeoutRef.current = setTimeout(() => {
            setIsProcessing(false);
            setPaymentError('לא התקבלה תשובה מהשרת. נסה שנית.');
        }, 25000);

        const groupe = donationType === 'machatzit' ? 'מחצית השקל-אתר' : 'מתנות לאביונים אתר';
        const donation = {
            Mosad: NEDARIM_CONFIG.Mosad,
            ApiValid: NEDARIM_CONFIG.ApiValid,
            Zeout: idNumber || '',
            FirstName: firstName,
            LastName: lastName,
            Street: '',
            City: '',
            Phone: phone,
            Mail: email || '',
            PaymentType: 'Ragil',
            Amount: Math.round(finalAmount).toString(),
            Tashlumim: '1',
            Currency: NEDARIM_CONFIG.Currency,
            Groupe: groupe,
            Comment: dedication || '',
            Param1: '',
            Param2: '',
            CallBack: '',
            CallBackMailError: '',
        };

        try {
            iframeRef.current.contentWindow.postMessage(
                { Name: 'FinishTransaction2', Value: donation },
                'https://www.matara.pro'
            );
        } catch (err) {
            setIsProcessing(false);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            setPaymentError('שגיאה בשליחת הנתונים: ' + err.message);
        }
    };

    // Cross-sell: start the other path
    const startCrossSell = () => {
        const newType = donationType === 'machatzit' ? 'matanot' : 'machatzit';
        setDonationType(newType);
        setStep(2);
        setPaymentSuccess(false);
        setPaymentError(null);
        setShowIframe(false);
        setIframeLoaded(false);
        setIsProcessing(false);
        setNefashot(1);
        setMatamotAmount(50);
        setCustomAmount('');
        // Keep personal info for convenience
    };

    // Determine current visible step index (for progress dots)
    const getProgressStep = () => step;

    // ===== RENDER STEPS =====

    const handleTypeSelect = (type) => {
        setDonationType(type);
        // On mobile, auto-advance to next step
        if (window.innerWidth <= 768) {
            setTimeout(() => setStep(2), 200);
        }
    };

    const renderStep1 = () => (
        <div className="step-card" key="step1">
            <h2 className="step-title">לאיזו מצווה תרצו לתרום?</h2>
            <p className="step-subtitle">בחרו את סוג התרומה</p>
            <div className="type-cards">
                <div
                    className={`type-card ${donationType === 'machatzit' ? 'type-card--selected' : ''}`}
                    onClick={() => handleTypeSelect('machatzit')}
                >
                    <span className="type-card-icon">🪙</span>
                    <span className="type-card-title">זכר למחצית השקל</span>
                    <span className="type-card-desc">פדיון 9 גרם כסף טהור לפי המחיר העדכני</span>
                </div>
                <div
                    className={`type-card ${donationType === 'matanot' ? 'type-card--selected' : ''}`}
                    onClick={() => handleTypeSelect('matanot')}
                >
                    <span className="type-card-icon">🤲</span>
                    <span className="type-card-title">מתנות לאביונים</span>
                    <span className="type-card-desc">המעות ימסרו ביום הפורים לאביונים בני תורה</span>
                </div>
            </div>
            <div className="step-nav">
                <button className="btn-back" onClick={goBack}>
                    <ArrowRight size={16} />
                    חזרה לדף הראשי
                </button>
                <button className="btn-next" onClick={goNext} disabled={!donationType}>
                    המשך
                    <ChevronLeft size={18} />
                </button>
            </div>
        </div>
    );

    const renderStep2Machatzit = () => (
        <div className="step-card" key="step2m">
            <h2 className="step-title">עבור כמה נפשות?</h2>
            <p className="step-subtitle">מעות הפדיון יעברו להחזקת תורה בכולל שערי ניסים רמת גן</p>

            <div className="counter-section">
                <span className="counter-label">מספר נפשות</span>
                <div className="counter">
                    <button className="counter-btn" onClick={() => setNefashot(Math.max(1, nefashot - 1))}>−</button>
                    <span className="counter-value">{nefashot}</span>
                    <button className="counter-btn" onClick={() => setNefashot(nefashot + 1)}>+</button>
                </div>
            </div>

            {/* Grams selector */}
            <div className="counter-section">
                <span className="counter-label">שיעור (גרם כסף)</span>
                <div className="grams-selector grams-selector--donate">
                    <button
                        className={`grams-option ${gramsOption === 9 ? 'grams-option--active' : ''}`}
                        onClick={() => setGramsOption(9)}
                    >
                        9 <br />גרם
                        {/* <span className="grams-option-desc">רוב הפוסקים</span> */}
                    </button>
                    <button
                        className={`grams-option ${gramsOption === 9.6 ? 'grams-option--active' : ''}`}
                        onClick={() => setGramsOption(9.6)}
                    >
                        9.6 <br /> גרם
                        {/* <span className="grams-option-desc">שיטת חב״ד</span> */}
                    </button>
                </div>
            </div>

            <div className="vat-toggle-row">
                <label className="vat-checkbox">
                    <input type="checkbox" checked={includeVat} onChange={(e) => setIncludeVat(e.target.checked)} />
                    <span className="vat-slider"></span>
                </label>
                <span className="vat-label">כולל מע"מ (18%)</span>
            </div>

            <div className="calc-amount">
                <div className="calc-amount-label">
                    סה"כ לתשלום {includeVat ? '(כולל מע"מ)' : '(ללא מע"מ)'}
                </div>
                <div className="calc-amount-value">{formatCurrency(machatzitAmount)}</div>
                <div className="calc-amount-detail">
                    {nefashot} {nefashot === 1 ? 'נפש' : 'נפשות'} × {formatCurrency(pricePerGram * gramsOption)}
                    {includeVat ? ' + 18% מע"מ' : ''}
                </div>
            </div>

            <div className="step-nav">
                <button className="btn-back" onClick={goBack}>
                    <ArrowRight size={16} />
                    חזרה
                </button>
                <button className="btn-next" onClick={goNext}>
                    המשך לפרטים אישיים
                    <ChevronLeft size={18} />
                </button>
            </div>
        </div>
    );

    const renderStep2Matanot = () => (
        <div className="step-card" key="step2a">
            <h2 className="step-title">כמה תרצו לתרום?</h2>
            <p className="step-subtitle">מתנות לאביונים — סכום לבחירתכם</p>

            <div className="amount-presets">
                {MATANOT_PRESETS.map((preset) => (
                    <button
                        key={preset.value}
                        className={`amount-preset ${matamotAmount === preset.value && !customAmount ? 'amount-preset--selected' : ''}`}
                        onClick={() => { setMatamotAmount(preset.value); setCustomAmount(''); }}
                    >
                        ₪{preset.value}
                        {preset.label && <span className="amount-preset-label">{preset.label}</span>}
                    </button>
                ))}
            </div>

            <div className="custom-amount-row">
                <input
                    type="number"
                    className="custom-amount-input"
                    placeholder="סכום אחר"
                    value={customAmount}
                    onChange={(e) => {
                        setCustomAmount(e.target.value);
                        if (e.target.value) setMatamotAmount(Number(e.target.value) || 50);
                    }}
                    min="1"
                />
            </div>

            <div className="calc-amount">
                <div className="calc-amount-label">סכום התרומה</div>
                <div className="calc-amount-value">{formatCurrency(matamotAmount)}</div>
            </div>

            <div className="rabbi-note">
                המעות ימסרו בו ביום לאביונים בני תורה ע"י מורנו הרה"ג יוסף שמעון פרטוש שליט"א
            </div>

            <div className="step-nav">
                <button className="btn-back" onClick={goBack}>
                    <ArrowRight size={16} />
                    חזרה
                </button>
                <button className="btn-next" onClick={goNext} disabled={!matamotAmount || matamotAmount <= 0}>
                    המשך לפרטים אישיים
                    <ChevronLeft size={18} />
                </button>
            </div>
        </div>
    );

    const renderStep3 = () => (
        <div className="step-card" key="step3">
            <h2 className="step-title">פרטים אישיים</h2>
            <p className="step-subtitle">
                סכום לתשלום: <strong style={{ color: 'var(--color-gold)' }}>{formatCurrency(finalAmount)}</strong>
            </p>

            <div className="form-grid">
                <div className="form-field">
                    <label>שם פרטי <span className="form-required">*</span></label>
                    <input
                        type="text"
                        className={`form-input ${errors.firstName ? 'form-input--error' : ''}`}
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="ישראל"
                    />
                    {errors.firstName && <span className="form-error">{errors.firstName}</span>}
                </div>
                <div className="form-field">
                    <label>שם משפחה <span className="form-required">*</span></label>
                    <input
                        type="text"
                        className={`form-input ${errors.lastName ? 'form-input--error' : ''}`}
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="ישראלי"
                    />
                    {errors.lastName && <span className="form-error">{errors.lastName}</span>}
                </div>
                <div className="form-field">
                    <label>
                        ת.ז. <span className="form-optional">(לקבלה לפי סעיף 46)</span>
                    </label>
                    <input
                        type="text"
                        className="form-input"
                        value={idNumber}
                        onChange={(e) => setIdNumber(e.target.value)}
                        placeholder="מספר תעודת זהות"
                        maxLength={9}
                    />
                </div>
                <div className="form-field">
                    <label>טלפון <span className="form-required">*</span></label>
                    <input
                        type="tel"
                        className={`form-input ${errors.phone ? 'form-input--error' : ''}`}
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="050-1234567"
                    />
                    {errors.phone && <span className="form-error">{errors.phone}</span>}
                </div>
                <div className="form-field form-field--full">
                    <label>
                        מייל <span className="form-optional">(לקבלת חשבונית וקבלה)</span>
                    </label>
                    <input
                        type="email"
                        className={`form-input ${errors.email ? 'form-input--error' : ''}`}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="email@example.com"
                    />
                    {errors.email && <span className="form-error">{errors.email}</span>}
                </div>
                <div className="form-field form-field--full">
                    <label>הקדשה <span className="form-optional">(לא חובה)</span></label>
                    <textarea
                        className="form-input"
                        value={dedication}
                        onChange={(e) => setDedication(e.target.value)}
                        placeholder="לרפואת / לעילוי נשמת / לזכות..."
                    />
                </div>
            </div>

            <div className="step-nav">
                <button className="btn-back" onClick={goBack}>
                    <ArrowRight size={16} />
                    חזרה
                </button>
                <button className="btn-next" onClick={goNext}>
                    המשך לתשלום
                    <CreditCard size={18} />
                </button>
            </div>
        </div>
    );

    const renderStep4 = () => {
        const typeLabel = donationType === 'machatzit' ? 'זכר למחצית השקל' : 'מתנות לאביונים';
        return (
            <div className="step-card" key="step4">
                <h2 className="step-title">תשלום</h2>

                <div className="summary-box">
                    <div className="summary-title">סיכום התרומה</div>
                    <div className="summary-row">
                        <span className="summary-row-label">סוג תרומה</span>
                        <span className="summary-row-value">{typeLabel}</span>
                    </div>
                    {donationType === 'machatzit' && (
                        <div className="summary-row">
                            <span className="summary-row-label">מספר נפשות</span>
                            <span className="summary-row-value">{nefashot}</span>
                        </div>
                    )}
                    <div className="summary-row">
                        <span className="summary-row-label">שם התורם</span>
                        <span className="summary-row-value">{firstName} {lastName}</span>
                    </div>
                    {dedication && (
                        <div className="summary-row">
                            <span className="summary-row-label">הקדשה</span>
                            <span className="summary-row-value">{dedication}</span>
                        </div>
                    )}
                    <div className="summary-row summary-total">
                        <span className="summary-row-label">סה"כ לתשלום</span>
                        <span className="summary-row-value">{formatCurrency(finalAmount)}</span>
                    </div>
                </div>

                {/* Nedarim Plus Iframe */}
                {showIframe ? (
                    <>
                        {iframeLoaded ? (
                            <div className="iframe-wrapper">
                                <iframe
                                    ref={iframeRef}
                                    id="nedarimIframe"
                                    src="https://www.matara.pro/nedarimplus/iframe/"
                                    title="תשלום מאובטח"
                                    allow="payment"
                                    style={{ height: '350px' }}
                                />
                            </div>
                        ) : (
                            <div className="iframe-loading">
                                <div className="iframe-spinner"></div>
                                <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>טוען את עמוד התשלום...</span>
                            </div>
                        )}

                        {iframeLoaded && !isProcessing && (
                            <button className="btn-pay" onClick={executePayment}>
                                <Heart size={18} style={{ display: 'inline', verticalAlign: 'middle', marginLeft: '6px' }} />
                                בצע תשלום — {formatCurrency(finalAmount)}
                            </button>
                        )}

                        {isProcessing && (
                            <div className="processing-overlay">
                                <div className="iframe-spinner"></div>
                                <span className="processing-text">מבצע חיוב, אנא המתן...</span>
                            </div>
                        )}

                        {paymentError && (
                            <div className="payment-error">{paymentError}</div>
                        )}
                    </>
                ) : (
                    <div className="iframe-loading">
                        <div className="iframe-spinner"></div>
                        <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>מכין את עמוד התשלום...</span>
                    </div>
                )}

                <div className="step-nav" style={{ marginTop: 'var(--space-lg)' }}>
                    <button className="btn-back" onClick={goBack} disabled={isProcessing}>
                        <ArrowRight size={16} />
                        חזרה
                    </button>
                </div>
            </div>
        );
    };

    const renderStep5 = () => {
        const otherType = donationType === 'machatzit' ? 'matanot' : 'machatzit';
        const otherLabel = donationType === 'machatzit' ? 'מתנות לאביונים' : 'זכר למחצית השקל';
        const otherIcon = donationType === 'machatzit' ? '🤲' : '🪙';

        return (
            <div className="step-card" key="step5">
                <div className="success-screen">
                    <div className="success-icon">✅</div>
                    <h2 className="success-title">תודה רבה על תרומתכם!</h2>
                    <p className="success-message">
                        התשלום התקבל בהצלחה.
                        <br />
                        {donationType === 'machatzit'
                            ? 'מעות הפדיון יעברו להחזקת תורה בכולל שערי ניסים.'
                            : 'המעות ימסרו ביום הפורים לאביונים בני תורה.'}
                        <br />
                        תזכו למצוות! 🙏
                    </p>

                    {/* Cross-sell */}
                    <div className="cross-sell-card">
                        <div className="cross-sell-title">
                            {otherIcon} רוצים לתרום גם {otherLabel}?
                        </div>
                        <p className="cross-sell-text">
                            {otherType === 'matanot'
                                ? 'המעות ימסרו בו ביום לאביונים בני תורה ע"י מורנו הרה"ג יוסף שמעון פרטוש שליט"א'
                                : 'פדיון 9 גרם כסף טהור — לפי המחיר העדכני בזמן אמת'}
                        </p>
                        <button className="btn-secondary" onClick={startCrossSell}>
                            {otherIcon} תרום {otherLabel}
                        </button>
                    </div>

                    <button className="btn-home" onClick={onBack}>
                        <Home size={14} style={{ display: 'inline', verticalAlign: 'middle', marginLeft: '4px' }} />
                        חזרה לדף הראשי
                    </button>
                </div>
            </div>
        );
    };

    // ===== Render current step =====
    const renderCurrentStep = () => {
        switch (step) {
            case 1: return renderStep1();
            case 2: return donationType === 'machatzit' ? renderStep2Machatzit() : renderStep2Matanot();
            case 3: return renderStep3();
            case 4: return renderStep4();
            case 5: return renderStep5();
            default: return renderStep1();
        }
    };

    return (
        <div className="donate-page" dir="rtl">
            {/* Header */}
            <header className="header">
                <div className="header-inner">
                    <div className="header-brand">
                        <div className="header-icon" style={{ background: 'transparent', boxShadow: 'none' }}>
                            <img src="/logoKolel.png" alt="logo" style={{ width: '130%', height: '130%', objectFit: 'contain' }} />
                        </div>
                        <div>
                            <h1 className="header-title">כולל שערי ניסים</h1>
                            <div className="header-subtitle">
                                <span>ארגון צח"י — רמת גן</span>
                            </div>
                        </div>
                    </div>
                    <button className="btn-back" onClick={onBack} style={{ border: 'none', padding: '8px 12px' }}>
                        <Home size={16} />
                    </button>
                </div>
            </header>

            <main className="donate-main">
                {/* Progress dots */}
                {step < 5 && (
                    <div className="wizard-progress">
                        {[1, 2, 3, 4].map((s, i) => (
                            <React.Fragment key={s}>
                                {i > 0 && <div className={`progress-line ${getProgressStep() > s - 1 ? 'progress-line--done' : ''}`} />}
                                <div className={`progress-dot ${getProgressStep() === s ? 'progress-dot--active' : ''} ${getProgressStep() > s ? 'progress-dot--done' : ''}`} />
                            </React.Fragment>
                        ))}
                    </div>
                )}

                {renderCurrentStep()}
            </main>

            <footer className="footer">
                <p>כולל שערי ניסים — ארגון צח"י | תשלום מאובטח באמצעות נדרים פלוס</p>
            </footer>
        </div>
    );
};

export default DonatePage;
